import { useState, useEffect, useRef } from 'react';
import { useSelector } from '@legendapp/state/react';
import { getAllTags } from '@/stores/notes$';
import { cn } from '@/lib/utils';

export default function TagAutocomplete({ noteId, onAddTag, onClose }) {
  const [input, setInput] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  // Get all existing tags and filter based on input
  const allTags = getAllTags();
  const suggestions = input.trim()
    ? allTags
        .filter(tag => tag.toLowerCase().includes(input.toLowerCase()))
        .sort((a, b) => {
          // Prioritize tags that start with the input
          const aStarts = a.toLowerCase().startsWith(input.toLowerCase());
          const bStarts = b.toLowerCase().startsWith(input.toLowerCase());
          if (aStarts && !bStarts) return -1;
          if (!aStarts && bStarts) return 1;
          return a.localeCompare(b);
        })
        .slice(0, 10)
    : allTags.slice(0, 10);

  useEffect(() => {
    // Auto-focus input when component mounts
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    // Reset selected index when suggestions change
    setSelectedIndex(0);
  }, [suggestions.length]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length > 0 && selectedIndex < suggestions.length) {
        // Select suggestion
        onAddTag(suggestions[selectedIndex]);
      } else if (input.trim()) {
        // Add new tag
        onAddTag(input.trim());
      }
      setInput('');
      setSelectedIndex(0);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === ',' || e.key === ' ') {
      e.preventDefault();
      if (input.trim()) {
        onAddTag(input.trim());
        setInput('');
        setSelectedIndex(0);
      }
    }
  };

  const handleSuggestionClick = (tag) => {
    onAddTag(tag);
    setInput('');
    setSelectedIndex(0);
    inputRef.current?.focus();
  };

  return (
    <div className="relative inline-block">
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={(e) => {
          // Only close if clicking outside (not on suggestions)
          if (!e.relatedTarget?.closest('.tag-suggestions')) {
            setTimeout(() => onClose(), 200);
          }
        }}
        placeholder="Add tag..."
        className="px-2 py-1 text-xs border border-primary/50 rounded bg-background focus:outline-none focus:ring-1 focus:ring-primary/50 min-w-[120px]"
        title={input.length > 50 ? input : ''}
      />

      {suggestions.length > 0 && input.trim() && (
        <div className="tag-suggestions absolute top-full left-0 mt-1 w-48 bg-popover border border-border rounded-lg shadow-lg py-1 z-50 max-h-48 overflow-y-auto">
          {suggestions.map((tag, index) => (
            <button
              key={tag}
              onClick={() => handleSuggestionClick(tag)}
              className={cn(
                "w-full text-left px-3 py-1.5 text-xs hover:bg-accent transition-colors",
                index === selectedIndex && "bg-accent"
              )}
            >
              #{tag}
            </button>
          ))}
        </div>
      )}

      <div className="absolute top-full left-0 mt-1 text-xs text-muted-foreground whitespace-nowrap">
        Press Enter, Comma, or Space to add
      </div>
    </div>
  );
}
